import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { analytics } from '@/lib/analytics';

// --- localStorage helpers (SSR-safe) ---

function safeGetStorage(key: string): string | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetStorage(key: string, value: string): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.error('Failed to save to localStorage:', e);
  }
}

function safeRemoveStorage(key: string): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.error('Failed to remove from localStorage:', e);
  }
}

// --- Storage keys ---

const SPOTIFY_TOKEN_KEY = 'spotify-playback-token';
const SPOTIFY_EXPIRY_KEY = 'spotify-playback-token-expires';
const SPOTIFY_REFRESH_KEY = 'spotify-playback-refresh-token';

const SPOTIFY_USER_KEY = 'spotify-user';

const GOOGLE_USER_KEY = 'google-user';
const GOOGLE_TOKEN_KEY = 'google-access-token';
const GOOGLE_EXPIRY_KEY = 'google-token-expires';
const GOOGLE_REFRESH_KEY = 'google-refresh-token';

// --- Types ---

export interface SpotifyUser {
  name: string;
  email: string;
  picture: string;
}

export interface GoogleUser {
  name: string;
  email: string;
  picture: string;
}

export interface AuthContextValue {
  spotifyToken: string | null;
  spotifyUser: SpotifyUser | null;
  spotifyLogin: () => void;
  spotifyLogout: () => void;
  spotifyLoading: boolean;

  googleUser: GoogleUser | null;
  googleLogin: () => void;
  googleLogout: () => void;
  googleLoading: boolean;

  isHydrated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// --- OAuth popup helper ---

function openOAuthPopup(url: string, title: string) {
  const width = 600;
  const height = 700;
  const left = window.screen.width / 2 - width / 2;
  const top = window.screen.height / 2 - height / 2;
  window.open(url, title, `width=${width},height=${height},left=${left},top=${top}`);
}

// --- Provider ---

export function AuthProvider({ children }: { children: ReactNode }) {
  const [spotifyToken, setSpotifyToken] = useState<string | null>(null);
  const [spotifyUser, setSpotifyUser] = useState<SpotifyUser | null>(null);
  const [spotifyLoading, setSpotifyLoading] = useState(false);

  const [googleUser, setGoogleUser] = useState<GoogleUser | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  const [isHydrated, setIsHydrated] = useState(false);

  // ---- Hydrate from localStorage on mount ----

  useEffect(() => {
    // Spotify
    const sToken = safeGetStorage(SPOTIFY_TOKEN_KEY);
    const sExpiry = safeGetStorage(SPOTIFY_EXPIRY_KEY);
    if (sToken && sExpiry) {
      const remaining = parseInt(sExpiry, 10) - Date.now();
      if (remaining > 5 * 60 * 1000) {
        setSpotifyToken(sToken);
        const sUser = safeGetStorage(SPOTIFY_USER_KEY);
        if (sUser) {
          try { setSpotifyUser(JSON.parse(sUser)); } catch { /* ignore */ }
        }
      }
    }

    // Google
    const gUser = safeGetStorage(GOOGLE_USER_KEY);
    const gExpiry = safeGetStorage(GOOGLE_EXPIRY_KEY);
    const gRefresh = safeGetStorage(GOOGLE_REFRESH_KEY);
    if (gUser && gExpiry) {
      const remaining = parseInt(gExpiry, 10) - Date.now();
      if (remaining > 0) {
        try {
          setGoogleUser(JSON.parse(gUser));
        } catch { /* corrupted data, ignore */ }
      } else if (gRefresh) {
        // Access token expired but we have a refresh token — restore user
        // immediately and trigger a background refresh
        try {
          setGoogleUser(JSON.parse(gUser));
        } catch { /* corrupted data, ignore */ }
      } else {
        clearGoogleStorage();
      }
    } else if (gRefresh && gUser) {
      // No expiry stored but we have a refresh token — restore and refresh
      try {
        setGoogleUser(JSON.parse(gUser));
      } catch { /* corrupted data, ignore */ }
    }

    setIsHydrated(true);

    const parsedGoogle = (() => {
      try {
        return gUser ? JSON.parse(gUser) as GoogleUser : null;
      } catch {
        return null;
      }
    })();
    if (parsedGoogle?.email) {
      analytics.identify(parsedGoogle.email, { email: parsedGoogle.email, display_name: parsedGoogle.name, auth_provider: 'google' });
    }
  }, []);

  // ---- Spotify token refresh scheduling ----

  useEffect(() => {
    if (!spotifyToken || !isHydrated) return;

    const expiry = safeGetStorage(SPOTIFY_EXPIRY_KEY);
    const refreshToken = safeGetStorage(SPOTIFY_REFRESH_KEY);
    if (!expiry) return;

    const expiryTime = parseInt(expiry, 10);
    const timeUntilExpiry = expiryTime - Date.now();

    if (timeUntilExpiry <= 5 * 60 * 1000 && refreshToken) {
      refreshSpotifyToken(refreshToken);
      return;
    }

    if (timeUntilExpiry <= 0) {
      clearSpotifyStorage();
      setSpotifyToken(null);
      return;
    }

    const refreshTime = timeUntilExpiry - 5 * 60 * 1000;
    const timer = setTimeout(() => {
      if (refreshToken) refreshSpotifyToken(refreshToken);
    }, refreshTime);
    return () => clearTimeout(timer);
  }, [spotifyToken, isHydrated]);

  // ---- Google token refresh scheduling ----

  useEffect(() => {
    if (!googleUser || !isHydrated) return;

    const expiry = safeGetStorage(GOOGLE_EXPIRY_KEY);
    const refreshToken = safeGetStorage(GOOGLE_REFRESH_KEY);
    if (!refreshToken) return;

    const expiryTime = expiry ? parseInt(expiry, 10) : 0;
    const timeUntilExpiry = expiryTime - Date.now();

    if (timeUntilExpiry <= 5 * 60 * 1000) {
      refreshGoogleToken(refreshToken);
      return;
    }

    const refreshTime = timeUntilExpiry - 5 * 60 * 1000;
    const timer = setTimeout(() => {
      refreshGoogleToken(refreshToken);
    }, refreshTime);
    return () => clearTimeout(timer);
  }, [googleUser, isHydrated]);

  // ---- Fetch Spotify profile when token available ----

  useEffect(() => {
    if (!spotifyToken || spotifyUser) return;
    let cancelled = false;

    fetch('https://api.spotify.com/v1/me', {
      headers: { Authorization: `Bearer ${spotifyToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((profile) => {
        if (cancelled || !profile) return;
        const user: SpotifyUser = {
          name: profile.display_name || profile.id,
          email: profile.email || '',
          picture: profile.images?.[0]?.url || '',
        };
        setSpotifyUser(user);
        safeSetStorage(SPOTIFY_USER_KEY, JSON.stringify(user));
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [spotifyToken, spotifyUser]);

  // ---- Spotify helpers ----

  function clearSpotifyStorage() {
    safeRemoveStorage(SPOTIFY_TOKEN_KEY);
    safeRemoveStorage(SPOTIFY_EXPIRY_KEY);
    safeRemoveStorage(SPOTIFY_REFRESH_KEY);
    safeRemoveStorage(SPOTIFY_USER_KEY);
  }

  async function refreshSpotifyToken(refreshToken: string) {
    try {
      const res = await fetch('/api/spotify/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (res.ok) {
        const data = await res.json();
        safeSetStorage(SPOTIFY_TOKEN_KEY, data.accessToken);
        safeSetStorage(SPOTIFY_EXPIRY_KEY, String(Date.now() + data.expiresIn * 1000));
        if (data.refreshToken) safeSetStorage(SPOTIFY_REFRESH_KEY, data.refreshToken);
        setSpotifyToken(data.accessToken);
      } else {
        clearSpotifyStorage();
        setSpotifyToken(null);
      }
    } catch {
      clearSpotifyStorage();
      setSpotifyToken(null);
    }
  }

  const spotifyLogin = useCallback(async () => {
    setSpotifyLoading(true);
    try {
      const res = await fetch('/api/spotify/login');
      const data = await res.json();
      if (data.url) {
        openOAuthPopup(data.url, 'Spotify Login');
      } else {
        setSpotifyLoading(false);
      }
    } catch {
      setSpotifyLoading(false);
    }
  }, []);

  const spotifyLogout = useCallback(() => {
    clearSpotifyStorage();
    setSpotifyToken(null);
    setSpotifyUser(null);
  }, []);

  // ---- Google helpers ----

  function clearGoogleStorage() {
    safeRemoveStorage(GOOGLE_USER_KEY);
    safeRemoveStorage(GOOGLE_TOKEN_KEY);
    safeRemoveStorage(GOOGLE_EXPIRY_KEY);
    safeRemoveStorage(GOOGLE_REFRESH_KEY);
  }

  async function refreshGoogleToken(refreshToken: string) {
    try {
      const res = await fetch('/api/google/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (res.ok) {
        const data = await res.json();
        safeSetStorage(GOOGLE_TOKEN_KEY, data.accessToken);
        safeSetStorage(GOOGLE_EXPIRY_KEY, String(Date.now() + data.expiresIn * 1000));
      } else {
        clearGoogleStorage();
        setGoogleUser(null);
      }
    } catch {
      clearGoogleStorage();
      setGoogleUser(null);
    }
  }

  const googleLogin = useCallback(async () => {
    setGoogleLoading(true);
    try {
      const res = await fetch('/api/google/login');
      const data = await res.json();
      if (data.url) {
        openOAuthPopup(data.url, 'Google Sign In');
      } else {
        setGoogleLoading(false);
      }
    } catch {
      setGoogleLoading(false);
    }
  }, []);

  const googleLogout = useCallback(() => {
    clearGoogleStorage();
    setGoogleUser(null);
  }, []);

  // ---- Listen for OAuth popup messages ----

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Spotify
      if (event.data.type === 'spotify-auth-success') {
        const { accessToken, refreshToken, expiresIn } = event.data;
        safeSetStorage(SPOTIFY_TOKEN_KEY, accessToken);
        safeSetStorage(SPOTIFY_EXPIRY_KEY, String(Date.now() + expiresIn * 1000));
        if (refreshToken) safeSetStorage(SPOTIFY_REFRESH_KEY, refreshToken);
        setSpotifyToken(accessToken);
        setSpotifyLoading(false);
      } else if (event.data.type === 'spotify-auth-error') {
        setSpotifyLoading(false);
      }

      // Google
      if (event.data.type === 'google-auth-success') {
        const { user, accessToken, refreshToken, expiresIn } = event.data;
        safeSetStorage(GOOGLE_USER_KEY, JSON.stringify(user));
        safeSetStorage(GOOGLE_TOKEN_KEY, accessToken || '');
        safeSetStorage(GOOGLE_EXPIRY_KEY, String(Date.now() + (expiresIn || 3600) * 1000));
        if (refreshToken) safeSetStorage(GOOGLE_REFRESH_KEY, refreshToken);
        setGoogleUser(user);
        setGoogleLoading(false);
        if (user?.email) {
          analytics.identify(user.email, { email: user.email, display_name: user.name, auth_provider: 'google' });
          analytics.loginCompleted('google');
        }
      } else if (event.data.type === 'google-auth-error') {
        setGoogleLoading(false);
      }
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        spotifyToken,
        spotifyUser,
        spotifyLogin,
        spotifyLogout,
        spotifyLoading,
        googleUser,
        googleLogin,
        googleLogout,
        googleLoading,
        isHydrated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
